import {
  createCarRental,
  duplicateCarRental,
  getCarRental,
  updateCarRental,
} from '@/api/carRental.api';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { invalidateBenefitLists } from '@/hooks/useBenefits';
import { queryKeys } from '@/queryKeys';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

export const useCreateCarRental = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FormData) => createCarRental(data),
    onSuccess: () => {
      invalidateBenefitLists(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.carRentals.all });
    },
  });
};

export const useUpdateCarRental = (language?: SupportedLocale) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ benefitId, data }: { benefitId: string; data: FormData }) =>
      updateCarRental(benefitId, data, language),
    onSuccess: () => {
      invalidateBenefitLists(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.carRentals.all });
    },
  });
};

export const useGetCarRental = (benefitId: string) =>
  useQuery({
    queryKey: queryKeys.carRentals.detail(benefitId),
    queryFn: () => getCarRental(benefitId),
  });

export const useDuplicateCarRental = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      benefitId,
      fields,
    }: {
      benefitId: string;
      fields: string[];
    }) => duplicateCarRental(benefitId, fields),
    onSuccess: () => {
      invalidateBenefitLists(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.carRentals.all });
    },
  });
};

export const useApplyGlobalOptionsToCarRentalCategories = ({
  control,
  setValue,
  index,
  displayCategoryOptions,
}: {
  control: Control<CarRentalFormDataWithTranslations>;
  setValue: UseFormSetValue<CarRentalFormDataWithTranslations>;
  index: number;
  displayCategoryOptions: boolean;
}) => {
  const displayGlobalForm = useWatch({
    control,
    name: 'displayGlobalForm',
  });
  const insurances = useWatch({
    control,
    name: 'insurances',
  });
  const options = useWatch({
    control,
    name: 'options',
  });
  const taxes = useWatch({
    control,
    name: 'taxes',
  });
  const oneWayFees = useWatch({
    control,
    name: 'oneWayFees',
  });

  useEffect(() => {
    if (displayGlobalForm === 'true') {
      if (!displayCategoryOptions) {
        setValue(`carCategories.${index}.insurances`, insurances);
        setValue(`carCategories.${index}.options`, options);
        setValue(`carCategories.${index}.taxes`, taxes);
        setValue(`carCategories.${index}.oneWayFees`, oneWayFees);
      }
    }
  }, [
    displayCategoryOptions,
    displayGlobalForm,
    index,
    insurances,
    oneWayFees,
    options,
    setValue,
    taxes,
  ]);
};
